import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";

/**
 * CierreParcialModal — entrega/facturación parcial multi-ítem dentro de una OC/WO.
 *
 * Props:
 *   target  { co, focusItemId? } | null
 *           co: el CustomerOrder enriquecido (co.items con piId, qty,
 *               qtyDelivered, qtyInvoiced, deliveries[], workOrderId).
 *           focusItemId: piId del ítem que disparó la apertura — pre-llena
 *               qty con el pendiente. El usuario puede sumar otros ítems
 *               al mismo documento antes de submit.
 *   onClose ()
 *   onSaved (response)
 *   onPromoteGuia (deliveryTarget) — callback opcional cuando el usuario
 *           hace click en "+ facturar" sobre una guía ya registrada.
 *           Cierra este modal y abre PromoverGuiaModal con el delivery.
 *
 * Backend: PATCH /workOrder/:id/cerrar-parcial con items: [...].
 */
const CierreParcialModal = ({ target, onClose, onSaved, onPromoteGuia }) => {
  const [docType, setDocType]     = useState("guia");
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate]     = useState("");
  const [notes, setNotes]         = useState("");
  const [perItemQty, setPerItemQty] = useState({}); // { piId: "qty" }
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  useEffect(() => {
    if (!target?.co) return;
    setDocType("guia");
    setDocNumber("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setNotes("");
    setError("");
    // Pre-llenar qty: focusItemId con su pendiente, los demás en 0 (vacío).
    const initial = {};
    for (const it of (target.co.items || [])) {
      if (!it.piId) continue;
      const pending = Math.max(0, (it.qty || 0) - (it.qtyDelivered || 0) - (it.qtyInvoiced || 0));
      if (target.focusItemId && it.piId === target.focusItemId && pending > 0) {
        initial[it.piId] = String(pending);
      } else {
        initial[it.piId] = "";
      }
    }
    setPerItemQty(initial);
  }, [target]);

  if (!target?.co) return null;
  const co = target.co;
  // Mostrar items con piId vinculado que estén:
  //   (a) con qty pendiente > 0 — se pueden cerrar con nueva guía/factura, o
  //   (b) sin pendiente pero con alguna guía pendiente de facturación.
  const eligibleItems = (co.items || []).filter(it => {
    if (!it.piId) return false;
    const pending = Math.max(0, (it.qty || 0) - (it.qtyDelivered || 0) - (it.qtyInvoiced || 0));
    if (pending > 0) return true;
    return (it.deliveries || []).some(d => d.docType === "guia");
  });

  const submit = async () => {
    setError("");
    if (!docNumber.trim()) {
      setError("N° de documento es requerido");
      return;
    }
    // Construir array de items con qty > 0.
    const itemsPayload = [];
    for (const it of eligibleItems) {
      const qtyStr = perItemQty[it.piId] || "";
      const qty = Number(qtyStr);
      if (!qtyStr || qty === 0) continue;
      if (!Number.isFinite(qty) || qty < 0) {
        setError(`Cantidad inválida en ítem ${it.sku || it.name || it.piId}`);
        return;
      }
      const pending = Math.max(0, (it.qty || 0) - (it.qtyDelivered || 0) - (it.qtyInvoiced || 0));
      if (qty > pending) {
        setError(`No podés cerrar ${qty} en "${it.sku || it.name}" — solo quedan ${pending}`);
        return;
      }
      itemsPayload.push({
        piId: it.piId,
        qty,
        docType,
        docNumber: docNumber.trim(),
        docDate: docDate ? new Date(docDate).toISOString() : new Date().toISOString(),
        notes: notes.trim() || undefined,
      });
    }
    if (itemsPayload.length === 0) {
      setError("Ingresá cantidad en al menos un ítem");
      return;
    }

    // Todos los items van a la misma WO (la WO espejo de la CO).
    const woId = eligibleItems[0]?.workOrderId;
    if (!woId) {
      setError("No se encontró la WO espejo de la OC");
      return;
    }

    setSaving(true);
    try {
      const res = await backend.patch(`workOrder/${woId}/cerrar-parcial`, {
        items: itemsPayload,
      });
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Error al guardar");
    }
    setSaving(false);
  };

  const totalToClose = Object.values(perItemQty).reduce((s, v) => s + (Number(v) || 0), 0);
  const isInvoice = docType !== "guia";
  const accent = isInvoice ? "#2ecc71" : "#3498db";

  return ReactDOM.createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10000,
      }}
    >
      <div style={{
        background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
        width: "min(720px, 96vw)", maxHeight: "90vh", overflowY: "auto",
        padding: "18px 22px", color: "#ddd", fontSize: 12,
        fontFamily: "Arial, sans-serif",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
            Cierre parcial · OC {co.clientOCNumber || `N°${co.number}`}
          </div>
          <button
            onClick={() => !saving && onClose?.()}
            style={{ background:"transparent", border:"none", color: "#cbd5e1", fontSize:18, cursor:"pointer", lineHeight:1 }}
            title="Cerrar"
          >×</button>
        </div>

        <div style={{ fontSize: 11, color: "#cbd5e1", marginBottom: 12 }}>
          {co.client?.name || "—"} · {(eligibleItems.length || 0)} ítem{eligibleItems.length === 1 ? "" : "s"} con pendiente
        </div>

        {/* Header del documento — único para todos los items */}
        <div style={{
          background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
          padding: "12px 14px", marginBottom: 14,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#cbd5e1", textTransform:"uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            Datos del documento (aplica a todos los ítems abajo)
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap: 10 }}>
            <Field label="Tipo de documento">
              <select
                value={docType} onChange={e => setDocType(e.target.value)}
                className="bini-input" style={{ height: 32 }}
              >
                <option value="guia">Guía de despacho</option>
                <option value="factura">Factura</option>
                <option value="boleta">Boleta</option>
              </select>
            </Field>
            <Field label="N° documento *">
              <input
                className="bini-input bini-mono" value={docNumber}
                onChange={e => setDocNumber(e.target.value)}
                placeholder="ej: 12345"
                autoFocus
              />
            </Field>
            <Field label="Fecha">
              <input
                type="date" className="bini-input" value={docDate}
                onChange={e => setDocDate(e.target.value)}
              />
            </Field>
            <Field label="Notas (opcional)">
              <input
                className="bini-input" value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Lote, transportista, observaciones…"
              />
            </Field>
          </div>
        </div>

        {/* Lista de items con qty input por cada uno */}
        {eligibleItems.length === 0 ? (
          <div style={{ color:"#2ecc71", padding:"10px 0", textAlign:"center" }}>
            ✓ Todos los ítems de esta OC ya están completamente cerrados.
          </div>
        ) : (
          <div style={{
            background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6,
            padding: "8px 0", marginBottom: 14,
          }}>
            <div style={{
              display:"grid",
              gridTemplateColumns: "60px 1fr 80px 100px 90px",
              gap: 8, padding: "6px 14px", fontSize: 9.5, fontWeight: 700,
              color: "#cbd5e1", textTransform:"uppercase", letterSpacing: 0.5,
              borderBottom: "1px solid #2e2e2e",
            }}>
              <span>SKU</span>
              <span>Descripción</span>
              <span style={{ textAlign:"right" }}>Pendiente</span>
              <span style={{ textAlign:"right" }}>Cant a cerrar</span>
              <span style={{ textAlign:"right" }}>Estado</span>
            </div>
            {eligibleItems.map(it => {
              const total = it.qty || 0;
              const del = it.qtyDelivered || 0;
              const inv = it.qtyInvoiced  || 0;
              const pending = Math.max(0, total - del - inv);
              const qtyVal = perItemQty[it.piId] || "";
              const itemDeliveries = Array.isArray(it.deliveries) ? it.deliveries : [];
              return (
                <div key={it.piId} style={{ borderBottom: "1px solid #1f1f1f" }}>
                  <div style={{
                    display:"grid",
                    gridTemplateColumns: "60px 1fr 80px 100px 90px",
                    gap: 8, padding: "8px 14px", fontSize: 11,
                    alignItems: "center",
                  }}>
                    <span style={{ fontFamily:"monospace", color: it.sku ? "#3498db" : "#555" }}>
                      {it.sku || "—"}
                    </span>
                    <span style={{ color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                      title={it.name || it.desc || "(sin descripción)"}>
                      {it.name || it.desc || "(sin descripción)"}
                    </span>
                    <span style={{ textAlign:"right", color: pending > 0 ? "#f39c12" : "#666", fontWeight: 700 }}>
                      {pending} / {total}
                    </span>
                    <input
                      type="number" min={0} max={pending} value={qtyVal}
                      onChange={e => setPerItemQty(p => ({ ...p, [it.piId]: e.target.value }))}
                      placeholder={pending > 0 ? "0" : "—"}
                      disabled={pending === 0}
                      className="bini-input"
                      style={{
                        textAlign:"right",
                        opacity: pending === 0 ? 0.4 : 1,
                        cursor: pending === 0 ? "not-allowed" : "text",
                      }}
                      title={pending === 0 ? "Ya cerrado · usá '+ facturar' en la guía si querés actualizarla" : ""}
                    />
                    <span style={{ textAlign:"right", fontSize: 10, color: "#aaa" }}>
                      {del > 0 && <span style={{ color:"#3498db" }}>↗{del}g </span>}
                      {inv > 0 && <span style={{ color:"#2ecc71" }}>✓{inv}f</span>}
                      {del === 0 && inv === 0 && <span>—</span>}
                    </span>
                  </div>
                  {/* Deliveries ya registrados — guías facturables + facturas/boletas como audit */}
                  {itemDeliveries.length > 0 && (
                    <div style={{ padding: "0 14px 8px 78px", display:"flex", flexWrap:"wrap", gap: 6 }}>
                      {itemDeliveries.map(d => {
                        const isGuia = d.docType === "guia";
                        const color = isGuia ? "#3498db" : "#2ecc71";
                        return (
                          <div key={d._id} style={{
                            background:"#0f0f0f", border:`1px solid ${color}33`,
                            borderRadius: 4, padding:"3px 7px", fontSize: 10,
                            display:"inline-flex", alignItems:"center", gap: 5,
                          }}>
                            <span style={{ color, fontWeight: 700 }}>
                              {isGuia ? "📄 guía" : (d.docType === "factura" ? "✓ factura" : "✓ boleta")}
                            </span>
                            <span style={{ color:"#aaa", fontFamily:"monospace" }}>
                              {d.docNumber || "—"}
                            </span>
                            <span style={{ color: "#aaa" }}>· {d.qty}u</span>
                            {d.docDate && (
                              <span style={{ color: "#aaa" }}>
                                · {new Date(d.docDate).toLocaleDateString("es-CL", { day:"2-digit", month:"2-digit" })}
                              </span>
                            )}
                            {isGuia && onPromoteGuia && (
                              <button
                                onClick={() => {
                                  onPromoteGuia({
                                    workOrderId: it.workOrderId,
                                    deliveryId: d._id,
                                    qty: d.qty,
                                    guiaNumber: d.docNumber,
                                    guiaDate: d.docDate,
                                    itemLabel: `${it.sku ? it.sku + " · " : ""}${it.name || it.desc || ""}`,
                                  });
                                  onClose?.();
                                }}
                                style={{
                                  background:"#0d2a15", color:"#2ecc71",
                                  border:"1px solid #2ecc7155", borderRadius: 3,
                                  padding:"1px 6px", fontSize: 9, fontWeight: 700,
                                  cursor:"pointer", marginLeft: 2,
                                }}
                                title="Facturar esta guía"
                              >
                                + facturar
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {totalToClose > 0 && (
              <div style={{
                padding: "8px 14px", fontSize: 11, color: "#cbd5e1",
                borderTop: "1px solid #2e2e2e", textAlign: "right",
              }}>
                Total a cerrar con este documento: <strong style={{ color: accent }}>{totalToClose} unidad{totalToClose === 1 ? "" : "es"}</strong>
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            color:"#e74c3c", fontSize: 11, marginTop: 4,
            background:"rgba(231,76,60,0.08)", padding:"6px 10px", borderRadius: 4,
            border:"1px solid rgba(231,76,60,0.3)",
          }}>{error}</div>
        )}

        <div style={{ display:"flex", justifyContent:"flex-end", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            style={{
              background:"transparent", border:"1px solid #444",
              color:"#aaa", borderRadius: 5, padding:"7px 14px",
              fontSize: 12, cursor:"pointer",
            }}
          >Cancelar</button>
          <button
            onClick={submit}
            disabled={saving || eligibleItems.length === 0}
            style={{
              background: accent, border:"none", color:"#fff",
              borderRadius: 5, padding:"7px 16px",
              fontSize: 12, fontWeight: 600,
              cursor: saving ? "wait" : "pointer",
              opacity: saving || eligibleItems.length === 0 ? 0.6 : 1,
            }}
          >{saving ? "Guardando…" : `Registrar ${docType === "guia" ? "guía" : docType}`}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
      textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
    }}>{label}</div>
    {children}
  </div>
);

export default CierreParcialModal;
